/**
 * Copyright (c) 2025 George Nassef, Vector Engineering LLC
 * All rights reserved.
 * 
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 * Commercial use requires explicit written permission.
 */

import axios from 'axios';

// Log environment info
console.info('Environment:', {
  NODE_ENV: process.env.NODE_ENV,
  VERCEL: process.env.REACT_APP_VERCEL_DEPLOYMENT,
  API_URL: process.env.REACT_APP_API_URL,
  hostname: window.location.hostname
});

// Determine the API base URL based on the environment
const getBaseUrl = () => {
  // In production (Vercel), use relative URLs to avoid CORS issues
  if (process.env.NODE_ENV === 'production' && process.env.REACT_APP_VERCEL_DEPLOYMENT === 'true') {
    return '';
  }
  
  // For development or non-Vercel production, use the configured API URL
  const apiUrl = process.env.REACT_APP_API_URL;
  if (!apiUrl) {
    console.warn('REACT_APP_API_URL not set, falling back to localhost:8000');
  }
  return apiUrl || 'http://localhost:8000';
};

// Create an axios instance for the backend API
const baseURL = getBaseUrl();
console.log('Initializing API with baseURL:', baseURL);

const api = axios.create({
  baseURL,
  headers: {
    'Content-Type': 'application/json'
  },
  timeout: 60000,
  maxBodyLength: Infinity
});

// Add request interceptor for logging
api.interceptors.request.use(
  (config) => {
    // Log all API requests
    console.log('API request:', {
      url: config.url,
      method: config.method,
      env: {
        NODE_ENV: process.env.NODE_ENV,
        VERCEL: process.env.REACT_APP_VERCEL_DEPLOYMENT,
        hostname: window.location.hostname
      }
    });
    return config;
  },
  (error) => Promise.reject(error)
);

// Add response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Extract detailed error information
    const errorDetails = {
      message: error.response?.data?.message || error.message || 'Unknown error occurred',
      status: error.response?.status,
      url: error.config?.url,
      method: error.config?.method,
      timestamp: new Date().toISOString(),
      requestId: error.config?.headers?.['x-request-id'],
      env: {
        NODE_ENV: process.env.NODE_ENV,
        VERCEL: process.env.REACT_APP_VERCEL_DEPLOYMENT,
        hostname: window.location.hostname
      }
    };

    // Log error details
    console.error('API Error:', errorDetails);

    // Throw error with detailed message
    throw new Error(`API Error (${errorDetails.status}): ${errorDetails.message}`);
  }
);

// API interfaces
export interface ScriptPrompt {
  title: string;
  theme: string;
  setting: string;
  characters: Character[];
  context: string;
  educational_message?: string;
  target_audience: string;
  target_word_count?: number;
  duration?: number;
  animation_requirements?: {
    include_facial_expressions: boolean;
    include_body_language: boolean;
    include_camera_angles: boolean;
    include_voice_modulation: boolean;
  };
  project_id?: number;
}

export interface Character {
  name: string;
  age: number;
  description: string;
  modelPath?: string;
  previewImage?: string;
  voiceType?: 'teen_female_casual' | 'teen_female_energetic' | 'teen_female_preppy' | 'teen_female_shy';
}

export interface SelectedModel {
  characterId: string;
  modelPath: string;
  voiceType?: 'teen_female_casual' | 'teen_female_energetic' | 'teen_female_preppy' | 'teen_female_shy';
}

export interface Script {
  id: string;
  title: string;
  content: string;
  created_at: string;
  updated_at?: string;
  scene_setting: string;
  characters: Record<string, Character>;
  is_completed?: boolean;
  last_step?: number;
  project_id?: number;
  generation_time?: number;
  word_count?: number;
  is_draft?: boolean;
  is_active?: boolean;
  has_generated_content?: boolean;
}

export interface Project {
  id: number;
  title: string;
  theme?: string;
  setting?: string;
  context?: string;
  educational_message?: string;
  characters: Character[];
  selectedModels: SelectedModel[];
  last_step: number;
  created_at: string;
  updated_at: string;
  is_completed: boolean;
  script_id?: number;
  target_word_count?: number;
  estimated_duration?: number;
  tropes?: string[];
  category?: string;
}

// Default values for new projects
const defaultProject = {
  characters: [
    { name: '', age: 16, description: '', voiceType: 'teen_female_casual' },
    { name: '', age: 16, description: '', voiceType: 'teen_female_casual' }
  ],
  selectedModels: []
};

// Transform project data to ensure proper types
const transformProjectData = (data: any): Project => {
  return {
    ...defaultProject,
    ...data,
    characters: Array.isArray(data.characters) ? data.characters : defaultProject.characters,
    selectedModels: Array.isArray(data.selectedModels) ? data.selectedModels : defaultProject.selectedModels
  };
};

export interface ProjectCreate {
  title: string;
  theme?: string;
  setting?: string;
  context?: string;
  educational_message?: string;
  characters: Character[];
  selectedModels?: SelectedModel[];
  last_step: number;
  target_word_count?: number;
  estimated_duration?: number;
}

export interface ProjectUpdate {
  title?: string;
  theme?: string;
  setting?: string;
  context?: string;
  educational_message?: string;
  characters?: Character[];
  selectedModels?: SelectedModel[];
  last_step?: number;
  is_completed?: boolean;
  is_prompt_saved?: boolean;
  script_id?: number;
  target_word_count?: number;
  estimated_duration?: number;
  tropes?: string[];
  category?: string;
}

// Minimal chunk interface to reduce header size
interface ScriptChunk {
  id: string;
  seq: number;
  data: string;
  last: boolean;
}

// API methods
const apiService = {
  // Scripts
  getScripts: async (): Promise<Script[]> => {
    const response = await api.get('/api/scripts');
    return response.data;
  },

  getScript: async (id: string): Promise<Script> => {
    const response = await api.get(`/api/scripts/${id}`);
    return response.data;
  },

  createScript: async (scriptPrompt: ScriptPrompt): Promise<Script> => {
    // Clean and minimize the prompt data for direct submission
    const minimalPrompt = {
      title: scriptPrompt.title.substring(0, 100),
      theme: scriptPrompt.theme.substring(0, 100),
      setting: scriptPrompt.setting.substring(0, 100),
      characters: scriptPrompt.characters.map(c => ({
        name: c.name.substring(0, 50),
        age: c.age,
        description: c.description.substring(0, 200),
        modelPath: c.modelPath,
        voiceType: c.voiceType
      })),
      context: scriptPrompt.context.substring(0, 500),
      educational_message: scriptPrompt.educational_message?.substring(0, 200),
      target_audience: scriptPrompt.target_audience,
      project_id: scriptPrompt.project_id,
      timestamp: Date.now() // Force bypass cache
    };

    console.log('Sending script prompt:', minimalPrompt);

    // Direct submission approach
    try {
      const response = await api.post('/api/scripts/generate', minimalPrompt);
      console.log('Got script response:', response.data);
      return response.data;
    } catch (error: unknown) { // Explicitly type error as unknown
      console.error('Script generation error:', error);
      // Provide more context in the error message, checking error type
      let errorMessage = 'Unknown error during script generation';
      if (axios.isAxiosError(error)) {
        errorMessage = error.response?.data?.message || error.message;
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }
      throw new Error(`Script generation failed: ${errorMessage}`);
    }
  },

  updateScript: async (id: string, content: string): Promise<Script> => {
    const response = await api.put(`/api/scripts/${id}`, { content });
    return response.data;
  },

  // Projects
  getProjects: async (): Promise<Project[]> => {
    const response = await api.get('/api/projects');
    return response.data;
  },

  getProject: async (id: number): Promise<Project> => {
    const response = await api.get(`/api/projects/${id}`);
    return transformProjectData(response.data);
  },

  getLatestProject: async (): Promise<Project | null> => {
    try {
      const response = await api.get('/api/projects/latest');
      return response.data ? transformProjectData(response.data) : null;
    } catch (error) {
      return null;
    }
  },

  createProject: async (project: ProjectCreate): Promise<Project> => {
    const response = await api.post('/api/projects', project);
    return response.data;
  },

  updateProject: async (id: number, update: ProjectUpdate): Promise<Project> => {
    const response = await api.put(`/api/projects/${id}`, update);
    return response.data;
  },

  deleteProject: async (id: number): Promise<Project> => {
    const response = await api.delete(`/api/projects/${id}`);
    return response.data;
  }
};

export default apiService;
